import React, { useState, useEffect } from 'react';
import AdminNav from '../../components/admin/AdminNav';

const UserManagement = ({ backendUrl, onViewUserSubmissions }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('points'); // points, level, scans
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [searchTerm, setSearchTerm] = useState('');
  const [statsData, setStatsData] = useState({ total_users: 0, active_users: 0, total_points: 0, total_scans: 0 });
  const [regionalOfficers, setRegionalOfficers] = useState([]);
  const [savingAssignment, setSavingAssignment] = useState({}); // email -> boolean
  
  // 🆕 Duplicate points toggle state - persists across page refresh
  const [showAdjustedPoints, setShowAdjustedPoints] = useState(() => {
    return localStorage.getItem('adminShowAdjustedPoints') === 'true';
  });
  const [adjustedPointsData, setAdjustedPointsData] = useState([]);
  const [adjustedPointsLoading, setAdjustedPointsLoading] = useState(false);
  const [duplicateSettings, setDuplicateSettings] = useState({ enabled: false, available: false });
  
  // Persist adjusted points toggle to localStorage
  useEffect(() => {
    localStorage.setItem('adminShowAdjustedPoints', showAdjustedPoints ? 'true' : 'false');
  }, [showAdjustedPoints]);

  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  useEffect(() => {
    fetchUsers();
    loadRegionalOfficers();
    loadDuplicateSettings();
  }, []);
  
  // 🆕 Fetch adjusted points when toggle is ON
  useEffect(() => {
    if (showAdjustedPoints) {
      fetchAdjustedPoints();
    }
  }, [showAdjustedPoints]);
  
  // 🆕 Load duplicate points settings
  const loadDuplicateSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/duplicates/settings/points-adjustment`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      setDuplicateSettings(data);
    } catch (error) {
      console.error('Error loading duplicate settings:', error);
      setDuplicateSettings({ enabled: false, available: false });
    }
  };
  
  // 🆕 Fetch adjusted points for all users
  const fetchAdjustedPoints = async () => {
    setAdjustedPointsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/duplicates/adjusted-leaderboard`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success) {
        setAdjustedPointsData(data.users || []);
        console.log('✅ Loaded adjusted points for', data.users?.length, 'users');
      }
    } catch (error) {
      console.error('Error fetching adjusted points:', error);
      setAdjustedPointsData([]);
    } finally {
      setAdjustedPointsLoading(false);
    }
  };
  
  // 🆕 Get adjusted points for a specific user
  const getUserAdjustedData = (userEmail) => {
    if (!showAdjustedPoints || !adjustedPointsData.length) return null;
    return adjustedPointsData.find(u => u.user_email === userEmail);
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/stats`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      setUsers(data.users || []);
      setStatsData({
        total_users: data.total_users || 0,
        active_users: data.active_users || 0,
        total_points: data.total_points || 0,
        total_scans: data.total_scans || 0
      });

      // Update stats display
      if (data.total_users) console.log(`✅ ${data.total_users} users loaded`);
      if (data.active_users) console.log(`✅ ${data.active_users} active users (7d)`);
      if (data.total_points) console.log(`✅ ${data.total_points} total points awarded`);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRegionalOfficers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/regional-officers`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data?.success) {
        setRegionalOfficers(data.officers || []);
      } else {
        setRegionalOfficers([]);
      }
    } catch (e) {
      console.error('Error loading regional officers:', e);
      setRegionalOfficers([]);
    }
  };

  const handleAssignSalesLeader = async (userEmail, rslId, asmId = null, directAsmId = null) => {
    const email = (userEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) return;

    const rsl = (regionalOfficers || []).find(o => (o.id || o._id) === rslId) || null;
    const asm = asmId ? (regionalOfficers || []).find(o => (o.id || o._id) === asmId) : null;
    const directAsm = directAsmId ? (regionalOfficers || []).find(o => (o.id || o._id) === directAsmId) : null;

    setSavingAssignment(prev => ({ ...prev, [email]: true }));
    try {
      const token = localStorage.getItem('token');
      const encodedEmail = btoa(email);
      const res = await fetch(`${backendUrl}/api/admin/users/${encodedEmail}/sales-leader`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          sales_leader_id: rsl ? (rsl.id || rsl._id) : null,
          sales_leader_name: rsl ? rsl.name : '',
          sales_leader_email: rsl ? rsl.email : '',
          area_sales_manager_id: asm ? (asm.id || asm._id) : null,
          area_sales_manager_name: asm ? asm.name : '',
          area_sales_manager_email: asm ? asm.email : '',
          direct_asm_id: directAsm ? (directAsm.id || directAsm._id) : null,
          direct_asm_name: directAsm ? directAsm.name : '',
          direct_asm_email: directAsm ? directAsm.email : ''
        })
      });
      const j = await res.json();

      if (!res.ok || !j?.success) {
        console.error('API Error:', j);
        throw new Error(j?.detail || 'Failed to update assignment');
      }

      setUsers(prev => {
        const updated = prev.map(u => {
          if ((u.email || '').toLowerCase() !== email) return u;

          return {
            ...u,
            regional_sales_leader_id: j.regional_sales_leader_id,
            regional_sales_leader_name: j.regional_sales_leader_name,
            regional_sales_leader_email: j.regional_sales_leader_email,
            regional_sales_leader_assigned_at: j.regional_sales_leader_assigned_at,
            area_sales_manager_id: j.area_sales_manager_id || null,
            area_sales_manager_name: j.area_sales_manager_name || '',
            area_sales_manager_email: j.area_sales_manager_email || '',
            direct_asm_id: j.direct_asm_id || null,
            direct_asm_name: j.direct_asm_name || '',
            direct_asm_email: j.direct_asm_email || ''
          };
        });

        return updated;
      });

      // Show success message
      const userName = users.find(u => (u.email || '').toLowerCase() === email)?.name || 'User';
      const rslName = rsl?.name || 'Unassigned';
      const asmName = asm?.name || 'Direct to RVP';
      alert(`✅ ${userName} assigned to ${rslName}${asm ? ` → ${asmName}` : ''}`);

    } catch (e) {
      console.error('Assignment save failed:', e);
      alert(`❌ Failed to save assignment: ${e.message}`);
    } finally {
      setSavingAssignment(prev => ({ ...prev, [email]: false }));
    }
  };

  const getSortedUsers = () => {
    let sorted = [...users];

    // Filter by search term
    if (searchTerm) {
      sorted = sorted.filter(user =>
        (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'points':
          aVal = a.total_points || 0;
          bVal = b.total_points || 0;
          break;
        case 'level':
          aVal = a.level || 0;
          bVal = b.level || 0;
          break;
        case 'scans':
          aVal = a.total_scans ?? a.total_submissions ?? a.stats?.total_inspections ?? 0;
          bVal = b.total_scans ?? b.total_submissions ?? b.stats?.total_inspections ?? 0;
          break;
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        default:
          aVal = a.total_points || 0;
          bVal = b.total_points || 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-600">⇅</span>;
    return sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />
      <div className="pt-6 px-4 max-w-full">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">User Management</h1>
          <p className="text-gray-400 text-xs sm:text-sm">View and manage TagQuest users</p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-sm">Total Users</p>
            <p className="text-2xl font-bold text-[var(--pbfs-blue)]">{statsData.total_users}</p>
          </div>
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-sm">Active Users (7d)</p>
            <p className="text-2xl font-bold text-[var(--pbfs-green)]">{statsData.active_users}</p>
          </div>
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-sm">Total Points</p>
            <p className="text-2xl font-bold text-[var(--pbfs-green)]">{statsData.total_points.toLocaleString()}</p>
          </div>
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-sm">Total Scans</p>
            <p className="text-2xl font-bold text-[var(--pbfs-blue)]">{statsData.total_scans.toLocaleString()}</p>
          </div>
        </div>

        {/* 🆕 Duplicate Points Display Toggle - Inspired by Salesforce + HubSpot */}
        {duplicateSettings.available && (
          <div className="bg-gradient-to-br from-[var(--pbfs-navy)]/80 to-[var(--pbfs-blue)]/20 border border-white/20 rounded-xl p-5 mb-6 shadow-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Left: Title and Description */}
              <div className="flex-1 min-w-[250px]">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-2xl">⚖️</span>
                  <h3 className="text-lg font-bold text-white">Points Display Mode</h3>
                </div>
                <p className="text-white/60 text-sm ml-9">
                  {showAdjustedPoints 
                    ? 'Showing deduplicated points (same-user duplicates excluded)'
                    : 'Showing raw points (1 submission = 1 point)'
                  }
                </p>
              </div>

              {/* Right: Toggle Switch (Salesforce-style) */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-2 border border-white/10">
                  <span className={`text-sm font-medium transition-colors ${!showAdjustedPoints ? 'text-white' : 'text-white/40'}`}>
                    Raw Points
                  </span>
                  
                  {/* Toggle Switch */}
                  <button
                    onClick={() => setShowAdjustedPoints(!showAdjustedPoints)}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)] focus:ring-offset-2 focus:ring-offset-[var(--pbfs-navy)] ${
                      showAdjustedPoints ? 'bg-[var(--pbfs-green)]' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                        showAdjustedPoints ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  
                  <span className={`text-sm font-medium transition-colors ${showAdjustedPoints ? 'text-white' : 'text-white/40'}`}>
                    Adjusted Points
                  </span>
                </div>
                
                {/* Loading indicator */}
                {adjustedPointsLoading && (
                  <span className="text-white/60 text-sm animate-pulse">
                    Calculating...
                  </span>
                )}
              </div>
            </div>

            {/* Info Banner (HubSpot-style) */}
            {showAdjustedPoints && (
              <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-start gap-3">
                <span className="text-blue-400 text-lg">ℹ️</span>
                <div className="flex-1 text-sm text-blue-200">
                  <strong>Adjusted Points Mode:</strong> When the same user submits multiple scans of the same location, 
                  only their first submission counts toward their score. Different users scanning the same location each get full points.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Users Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">
              Loading users...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--pbfs-navy)]/80">
                  <tr>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-white/5"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Name <SortIcon column="name" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left">Email</th>
                    <th className="px-6 py-3 text-left">Regional Vice President</th>
                    <th className="px-6 py-3 text-left">Regional Director</th>
                    <th className="px-6 py-3 text-left">Area Sales Manager</th>
                    <th
                      className="px-6 py-3 text-center cursor-pointer hover:bg-white/5"
                      onClick={() => handleSort('points')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {showAdjustedPoints ? 'Raw Pts' : 'Points'} <SortIcon column="points" />
                      </div>
                    </th>
                    {/* 🆕 Adjusted Points Column (only when toggle ON) */}
                    {showAdjustedPoints && (
                      <>
                        <th className="px-6 py-3 text-center bg-[var(--pbfs-green)]/10">
                          <div className="flex items-center justify-center gap-2">
                            Adjusted <span className="text-xs opacity-60">✓</span>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-center bg-[var(--pbfs-green)]/10">
                          <div className="flex items-center justify-center gap-2">
                            Diff
                          </div>
                        </th>
                      </>
                    )}
                    <th
                      className="px-6 py-3 text-center cursor-pointer hover:bg-white/5"
                      onClick={() => handleSort('level')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        Level <SortIcon column="level" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-center cursor-pointer hover:bg-white/5"
                      onClick={() => handleSort('scans')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        Total Scans <SortIcon column="scans" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center">Last Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {getSortedUsers().map((user, index) => (
                    <tr key={user._id || index} className="hover:bg-white/5 border-b border-white/10">
                      <td className="px-6 py-4">
                        <button
                          onClick={() => onViewUserSubmissions && onViewUserSubmissions(user.name || user.email, user.email)}
                          className="font-semibold text-white hover:text-[var(--pbfs-green)] hover:underline transition-colors cursor-pointer text-left"
                          title="View user's submissions"
                        >
                          {user.name || 'Anonymous'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-gray-400">
                        {user.email && user.email.trim() !== '' && user.email !== 'No email' ? user.email : (
                          <span className="text-white/30 italic text-sm">No email captured</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          // Check if this user is already an RVP
                          const userEmail = (user.email || '').toLowerCase().trim();
                          const isRD = (regionalOfficers || []).find(
                            o => (o.email || '').toLowerCase().trim() === userEmail && 
                                 (o.role === 'regional_sales_leader' || !o.role)
                          );
                          
                          if (isRD) {
                            // User IS a Regional Director - show badge, not dropdown
                            return (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] rounded-lg text-sm font-semibold">
                                👔 Regional Vice President
                              </span>
                            );
                          }
                          
                          if (!userEmail || !userEmail.includes('@')) {
                            return <span className="text-white/30 text-sm italic">Needs email</span>;
                          }
                          
                          return (
                            <select
                              value={user.regional_sales_leader_id || ''}
                              disabled={!!savingAssignment[userEmail]}
                              onChange={(e) => {
                                const newRslId = e.target.value || '';
                                // Clear ASM when changing RSL
                                handleAssignSalesLeader(user.email, newRslId || null, null);
                              }}
                              className="w-full bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--pbfs-blue)] disabled:opacity-60"
                              style={{ colorScheme: 'dark' }}
                              title="Assign this user to a Regional Vice President"
                            >
                              <option value="" style={{ backgroundColor: '#00314F', color: '#fff' }}>Unassigned</option>
                              {(regionalOfficers || [])
                                .filter(o => o.role === 'regional_sales_leader' || !o.role)
                                .map((rsl) => (
                                  <option key={rsl.id || rsl._id} value={rsl.id || rsl._id} style={{ backgroundColor: '#00314F', color: '#fff' }}>
                                    {rsl.name}
                                  </option>
                                ))}
                            </select>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const userEmail = (user.email || '').toLowerCase().trim();
                          
                          // Check if this user is already an RD
                          const isASM = (regionalOfficers || []).find(
                            o => (o.email || '').toLowerCase().trim() === userEmail && 
                                 o.role === 'area_sales_manager'
                          );
                          
                          // Check if this user is an RVP (they don't have RD assignments)
                          const isRD = (regionalOfficers || []).find(
                            o => (o.email || '').toLowerCase().trim() === userEmail && 
                                 (o.role === 'regional_sales_leader' || !o.role)
                          );
                          
                          if (isRD) {
                            return <span className="text-white/30 text-sm">—</span>;
                          }
                          
                          if (isASM) {
                            // User IS an RD - show badge with their RVP
                            const parentRSL = (regionalOfficers || []).find(o => (o.id || o._id) === isASM.reports_to);
                            return (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--pbfs-blue)]/20 text-[var(--pbfs-blue)] rounded-lg text-sm font-semibold" title={parentRSL ? `Reports to ${parentRSL.name}` : ''}>
                                📋 Regional Director
                              </span>
                            );
                          }
                          
                          if (!userEmail || !userEmail.includes('@') || !user.regional_sales_leader_id) {
                            return <span className="text-white/30 text-sm">—</span>;
                          }
                          
                          const asmsUnderRsl = (regionalOfficers || []).filter(
                            o => o.role === 'area_sales_manager' && o.reports_to === user.regional_sales_leader_id
                          );
                          
                          return asmsUnderRsl.length > 0 ? (
                            <select
                              value={user.area_sales_manager_id || ''}
                              disabled={!!savingAssignment[userEmail]}
                              onChange={(e) => {
                                const newAsmId = e.target.value;
                                handleAssignSalesLeader(user.email, user.regional_sales_leader_id, newAsmId || null);
                              }}
                              className="w-full bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--pbfs-blue)] disabled:opacity-60"
                              style={{ colorScheme: 'dark' }}
                              title="Assign to a Regional Director under this RVP"
                            >
                              <option value="" style={{ backgroundColor: '#00314F', color: '#fff' }}>Direct to RVP</option>
                              {asmsUnderRsl.map((asm) => (
                                <option key={asm.id || asm._id} value={asm.id || asm._id} style={{ backgroundColor: '#00314F', color: '#fff' }}>
                                  {asm.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-white/40 text-sm italic">No RDs under this RVP</span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const userEmail = (user.email || '').toLowerCase().trim();
                          const isOfficer = (regionalOfficers || []).find(
                            o => (o.email || '').toLowerCase().trim() === userEmail
                          );
                          if (isOfficer) {
                            if (isOfficer.role === 'asm') {
                              const parentRD = (regionalOfficers || []).find(o => (o.id || o._id) === isOfficer.reports_to);
                              return (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--pbfs-gray)]/20 text-[var(--pbfs-gray)] rounded-lg text-sm font-semibold" title={parentRD ? `Reports to ${parentRD.name}` : ''}>
                                  📋 Area Sales Manager
                                </span>
                              );
                            }
                            return <span className="text-white/30 text-sm">—</span>;
                          }
                          if (!userEmail || !userEmail.includes('@') || !user.area_sales_manager_id) {
                            return <span className="text-white/30 text-sm">—</span>;
                          }
                          const asmsUnderRd = (regionalOfficers || []).filter(
                            o => o.role === 'asm' && o.reports_to === user.area_sales_manager_id
                          );
                          return asmsUnderRd.length > 0 ? (
                            <select
                              value={user.direct_asm_id || ''}
                              disabled={!!savingAssignment[userEmail]}
                              onChange={(e) => {
                                const newAsmId = e.target.value;
                                handleAssignSalesLeader(user.email, user.regional_sales_leader_id, user.area_sales_manager_id, newAsmId || null);
                              }}
                              className="w-full bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--pbfs-blue)] disabled:opacity-60"
                              style={{ colorScheme: 'dark' }}
                              title="Assign to an Area Sales Manager under this RD"
                            >
                              <option value="" style={{ backgroundColor: '#00314F', color: '#fff' }}>Direct to RD</option>
                              {asmsUnderRd.map((asm) => (
                                <option key={asm.id || asm._id} value={asm.id || asm._id} style={{ backgroundColor: '#00314F', color: '#fff' }}>
                                  {asm.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-white/40 text-sm italic">No ASMs under this RD</span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-yellow-400 font-bold">
                          {(user.total_points || 0).toLocaleString()}
                        </span>
                      </td>
                      
                      {/* 🆕 Adjusted Points Columns (Stripe-style comparison) */}
                      {showAdjustedPoints && (() => {
                        const adjustedData = getUserAdjustedData(user.email);
                        const rawPts = user.total_points || 0;
                        const adjPts = adjustedData?.adjusted_points ?? rawPts;
                        const diff = rawPts - adjPts;
                        
                        return (
                          <>
                            <td className="px-6 py-4 text-center bg-[var(--pbfs-green)]/5">
                              <span className="text-[var(--pbfs-green)] font-bold">
                                {adjPts.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center bg-[var(--pbfs-green)]/5">
                              {diff === 0 ? (
                                <span className="text-white/40 text-sm">-</span>
                              ) : (
                                <button
                                  onClick={() => onViewUserSubmissions && onViewUserSubmissions(user.name || user.email)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-500/20 border border-orange-500/30 text-orange-300 text-xs font-semibold hover:bg-orange-500/30 hover:border-orange-500/50 transition-all cursor-pointer active:scale-95"
                                  title={`View ${user.name}'s ${diff} duplicate submissions`}
                                >
                                  <span>⚠️</span>
                                  <span>-{diff}</span>
                                </button>
                              )}
                            </td>                          </>
                        );
                      })()}
                      
                      <td className="px-6 py-4 text-center">
                        <span className="bg-blue-500 px-3 py-1 rounded-full text-sm font-bold">
                          {user.level || 1}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {user.total_scans ?? user.total_submissions ?? user.stats?.total_inspections ?? 0}
                      </td>
                      <td className="px-6 py-4 text-center text-gray-400 text-sm">
                        {user.last_active && user.last_active !== 'Never'
                          ? (typeof user.last_active === 'string' ? user.last_active : new Date(user.last_active).toLocaleDateString())
                          : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {getSortedUsers().length === 0 && (
                <div className="p-8 text-center text-gray-400">
                  {searchTerm ? 'No users found matching your search' : 'No users yet'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;



